/**
 * Capability checks against the user's entitlements.
 *
 * `/v2/user` returns `entitlements`: a flat list of capability keys the backend
 * derives from the user's role, tier and access, plus anything granted
 * directly. In organization context the organization's grants replace the
 * user's own, except for platform administrators, who get `admin.all` in every
 * context. Ask `can(key)` from `useAuth()` rather than reading the list,
 * because only `can` knows that `admin.all` grants everything.
 *
 * The keys below mirror the backend vocabulary in
 * `src/app/entitlements/vocabulary.py` (Bi0dr0ne/biodroneportal, copied at
 * commit 20c9d450). Add a key here before using it; the backend is the source
 * of truth for what exists and who is granted it.
 */
export const ENTITLEMENTS = Object.freeze({
	// Products: which surfaces the caller is licensed to enter.
	PRODUCT_PORTAL: 'product.portal',
	PRODUCT_DSP: 'product.dsp',
	PRODUCT_TRAINING: 'product.training',
	PRODUCT_VIDEO: 'product.video',
	PRODUCT_FLIGHTPLANNER: 'product.flightplanner',
	PRODUCT_WMS: 'product.wms',
	PRODUCT_HIP: 'product.hip',

	// Projects
	PROJECT_CREATE: 'project.create',
	PROJECT_READ: 'project.read',
	PROJECT_UPDATE: 'project.update',
	PROJECT_DELETE: 'project.delete',
	DEMO_PROJECT_READ: 'demo_project.read',

	// Models
	MODEL_CREATE: 'model.create',
	MODEL_READ: 'model.read',
	MODEL_UPDATE: 'model.update',
	MODEL_DELETE: 'model.delete',

	// Annotations
	ANNOTATION_CREATE: 'annotation.create',
	ANNOTATION_READ: 'annotation.read',
	ANNOTATION_UPDATE: 'annotation.update',
	ANNOTATION_DELETE: 'annotation.delete',

	// Flight planner
	FLIGHT_PLAN_CREATE: 'flight_plan.create',
	FLIGHT_PLAN_READ: 'flight_plan.read',
	FLIGHT_PLAN_UPDATE: 'flight_plan.update',
	FLIGHT_PLAN_DELETE: 'flight_plan.delete',

	// Organizations
	ORG_CREATE: 'org.create',
	ORG_READ: 'org.read',
	ORG_UPDATE: 'org.update',
	ORG_DELETE: 'org.delete',

	// Actions that are not create/read/update/delete
	DETECTION_RUN: 'detection.run',
	MODEL_TRAIN: 'model.train',
	WMS_PUBLISH: 'wms.publish',

	// Platform administrator: grants every key.
	ADMIN_ALL: 'admin.all',
});

const KNOWN_KEYS = new Set(Object.values(ENTITLEMENTS));

// Unknown keys already reported, so a check inside a render logs once.
const reportedUnknownKeys = new Set();

const reportUnknownKey = key => {
	if (!import.meta.env.DEV || reportedUnknownKeys.has(key)) return;
	reportedUnknownKeys.add(key);
	console.error(
		`can(${JSON.stringify(key)}): not a known entitlement. Use a key from ENTITLEMENTS in src/utils/entitlements.js.`
	);
};

/**
 * Builds `can(key)` for a list of entitlements.
 *
 * Fails closed: anything other than an array grants nothing, and only exact
 * matches count. `admin.all` grants every key. In development a key missing
 * from ENTITLEMENTS is logged (for admins too, so whoever runs the app sees a
 * typo), but still answered from the list.
 *
 * @param {string[]} entitlements the `entitlements` list from `/v2/user`
 * @returns {(key: string) => boolean}
 */
export const createCan = entitlements => {
	const granted = new Set(
		Array.isArray(entitlements)
			? entitlements.filter(entitlement => typeof entitlement === 'string')
			: []
	);
	const isAdmin = granted.has(ENTITLEMENTS.ADMIN_ALL);

	return key => {
		if (!KNOWN_KEYS.has(key)) reportUnknownKey(key);
		return isAdmin || granted.has(key);
	};
};
